// src/native/voice-summary.ts
import { Capacitor } from '@capacitor/core';
import { Geolocation, Position } from '@capacitor/geolocation';

let activeWatchId: string | null = null;
let hasSpoken = false;

export interface ActivityLocation {
  id: number | string;
  title: string;
  location_name: string;
  city?: string;
  lat: number;
  lng: number;
  short_summary?: string;
}

const PROXIMITY_METERS = 200; // adjust if you want “closer”

function isNativeIos(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

// Simple haversine distance in meters
function distanceInMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // meters
  const toRad = (v: number) => (v * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

// Build the spoken summary string (Trauvo-flavoured)
function buildSummaryText(activity: ActivityLocation): string {
  const where = activity.location_name || activity.city || 'your adventure spot';
  const short = activity.short_summary?.trim();

  let line = `You’re almost at ${activity.title}, at ${where}.`;

  if (short) {
    line += ` ${short}`;
  }

  line += ` Go. Explore. Trauvo.`;

  return line;
}

/* ----------------------------------------------------------
 * Voice selection helpers (Web Speech)
 * --------------------------------------------------------*/

let cachedVoice: SpeechSynthesisVoice | null = null;

function selectBestVoice(): SpeechSynthesisVoice | null {
  if (cachedVoice) return cachedVoice;

  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    return null;
  }

  const voices = window.speechSynthesis.getVoices();
  if (!voices || !voices.length) return null;

  // 1. Prefer local enhanced / Siri / “natural” English voices
  const preferred = voices.find(v =>
    v.localService &&
    /^en(-|$)/i.test(v.lang) &&
    /siri|enhanced|premium|natural/i.test(v.name)
  );

  // 2. Otherwise any local English voice
  const localEn = voices.find(v =>
    v.localService && /^en(-|$)/i.test(v.lang)
  );

  // 3. Otherwise any English voice
  const anyEn = voices.find(v => /^en(-|$)/i.test(v.lang));

  cachedVoice = preferred || localEn || anyEn || null;

  if (cachedVoice) {
    console.debug(
      '[voice-summary] selected voice:',
      cachedVoice.name,
      cachedVoice.lang
    );
  } else {
    console.debug('[voice-summary] no suitable voice found, using default');
  }

  return cachedVoice;
}

/**
 * Speak using the Web Speech API (works in WKWebView + browsers),
 * picking the best available English voice.
 */
function speakWithWebSpeech(text: string) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    console.warn('[voice-summary] Web Speech API not available');
    return;
  }

  try {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-CA';

    const voice = selectBestVoice();
    if (voice) {
      utterance.voice = voice;
    }

    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      // ignore
    }

    window.speechSynthesis.speak(utterance);
  } catch (err) {
    console.warn('[voice-summary] Web Speech speak failed', err);
  }
}

// Optional: refresh cached voice when voices list changes
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.onvoiceschanged = () => {
    cachedVoice = null;
    selectBestVoice();
  };
}

/** 🔊 Instant voice summary – used by the voice icon button */
export async function playActivityVoiceSummary(
  activity: ActivityLocation
): Promise<void> {
  try {
    console.debug('[voice-summary] playActivityVoiceSummary called with', activity);

    const enabled = window.localStorage.getItem('voice_summary_enabled');
    if (enabled === 'false') {
      console.warn('[voice-summary] Voice summary disabled in settings');
      return;
    }

    const text = buildSummaryText(activity);
    console.debug('[voice-summary] speaking text:', text);

    speakWithWebSpeech(text);
  } catch (err) {
    console.warn('[voice-summary] playActivityVoiceSummary failed', err);
  }
}

/** 📍 Proximity-triggered summary – used when user taps Directions/Go */
export async function startProximityVoiceSummary(
  activity: ActivityLocation,
  opts?: { force?: boolean }
): Promise<void> {
  try {
    if (!isNativeIos() && !opts?.force) {
      console.debug('[voice-summary] Not iOS, skipping proximity unless forced');
      return;
    }

    const enabled = window.localStorage.getItem('voice_summary_enabled');
    if (enabled === 'false') {
      console.warn('[voice-summary] Voice summary disabled in settings');
      return;
    }

    await stopProximityVoiceSummary();
    hasSpoken = false;

    console.debug('[voice-summary] Starting proximity watcher for activity', activity);

    activeWatchId = await Geolocation.watchPosition(
      {
        enableHighAccuracy: true,
        timeout: 15_000,
        maximumAge: 10_000,
      },
      async (pos: Position | null, err?: any) => {
        if (err) {
          console.warn('[voice-summary] Geolocation error', err);
          return;
        }
        if (!pos) return;

        const { latitude, longitude } = pos.coords;
        const distance = distanceInMeters(
          latitude,
          longitude,
          activity.lat,
          activity.lng
        );

        console.debug('[voice-summary] distance to activity (m):', distance);

        if (!hasSpoken && distance <= PROXIMITY_METERS) {
          hasSpoken = true;

          try {
            const text = buildSummaryText(activity);
            console.debug('[voice-summary] proximity speaking text:', text);
            speakWithWebSpeech(text);
          } catch (ttsErr) {
            console.warn('[voice-summary] speak failed from proximity', ttsErr);
          }

          await stopProximityVoiceSummary();
        }
      }
    );
  } catch (e) {
    console.warn('[voice-summary] startProximityVoiceSummary failed', e);
  }
}

export async function stopProximityVoiceSummary(): Promise<void> {
  try {
    if (activeWatchId !== null) {
      console.debug('[voice-summary] Clearing active watch', activeWatchId);
      await Geolocation.clearWatch({ id: activeWatchId });
      activeWatchId = null;
    }
    hasSpoken = false;
  } catch (e) {
    console.warn('[voice-summary] stopProximityVoiceSummary failed', e);
  }
}

/** 🌍 Expose helpers on window so activity-app.php JS can call them */

declare global {
  interface Window {
    playActivityVoiceSummary?: (activity: ActivityLocation) => Promise<void>;
    startProximityVoiceSummary?: (
      activity: ActivityLocation,
      opts?: { force?: boolean }
    ) => Promise<void>;
  }
}

if (typeof window !== 'undefined') {
  (window as any).playActivityVoiceSummary = playActivityVoiceSummary;
  (window as any).startProximityVoiceSummary = startProximityVoiceSummary;
  console.debug('[voice-summary] Helpers attached to window (Web Speech)');
}